import { useEffect, useMemo, useState } from "react";
import React from "react";
import { Dropdown, Form, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import Lister from "../../../../components/Lister/Lister";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import { useSession } from "../../../../contexts/SessionContext";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { TutorredCohortFormListItem, TutorredCohortForms } from "./UserTutors.d";
import {
  createUserTutor,
  fetchUserTutorredCohortForms,
  fetchUserTutorredCohorts,
  fetchUserTutors,
  generateUserTutorredCohortFormDocument,
  goToPMLink,
  openMailClientForCohortLeader,
  renderDocumentFilenames,
} from "./UserTutors.function";
import "./UserTutors.scss";
import TutorFormModal from "./components/TutorFormModal/TutorFormModal";

const _defaultTutorDocuments = [
  { name: "Présentation Liora", link: "/share/tutorship/PRESENTATION_LIORA_ALTERNANCE_06082026.pdf" },
  {
    name: "Livret du tuteur",
    link: "https://drive.google.com/file/d/1H9IJMgDltxao7XbaXtWrCjAxJOyeI2dE/view?usp=sharing",
  },
  {
    name: "Livret alternant",
    link: "https://drive.google.com/file/d/1jIChNGeXo1qqnF0vWiPIjF4JfEsKVNTX/view?usp=sharing",
  },
];

const UserTutors = ({
  language,
  tutorredCohortIds,
  userApprenticeshipCohorts,
  userId,
}: {
  language: string;
  tutorredCohortIds: number[];
  userApprenticeshipCohorts: any[];
  userId: number;
}) => {
  const { user_id: currentUserId } = useSession();
  const [tutors, setTutors] = useState<any[]>([]);
  const [loading, setLoading] = useState({ cohorts: true, tutors: true });
  const [showModal, setShowModal] = useState({ history: false, creation: false });
  const [selectedCohortId, setSelectedCohortId] = useState<number>();
  const [tutorredCohorts, setTutorredCohorts] = useState<{ [id: number]: string[] }>({});
  const [form, setForm] = useState({ cohortId: null, tutorId: null });
  const [isCreating, setIsCreating] = useState(false);
  const [forms, setForms] = useState<TutorredCohortForms | null>(null);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formToComplete, setFormToComplete] = useState<TutorredCohortFormListItem | null>(null);
  const [generatingFormKey, setGeneratingFormKey] = useState<"half" | "full" | null>(null);

  const _availableCohortIds = useMemo(() => {
    const fetchedCohortIds = Object.keys(tutorredCohorts).map(Number);
    return fetchedCohortIds.length ? fetchedCohortIds : tutorredCohortIds;
  }, [tutorredCohorts, tutorredCohortIds]);

  const _activeTutorredCohorts = useMemo(
    () => userApprenticeshipCohorts.filter((cohort) => _availableCohortIds.includes(cohort.id)),
    [_availableCohortIds, userApprenticeshipCohorts]
  );

  const chosenCohortId = selectedCohortId || _activeTutorredCohorts[0]?.id;

  useEffect(() => {
    fetchUserTutors(userId, setTutors, setLoading);
    fetchUserTutorredCohorts(userId, setTutorredCohorts, setLoading);
  }, [userId]);

  useEffect(() => {
    setForm({ cohortId: null, tutorId: null });
  }, [showModal.creation]);

  useEffect(() => {
    setForms(null);
    if (!chosenCohortId) {
      setFormsLoading(false);
      return;
    }
    fetchUserTutorredCohortForms(userId, chosenCohortId, setForms, setFormsLoading);
  }, [chosenCohortId, userId]);

  const _formItems = useMemo(
    () =>
      forms
        ? [
            {
              form: forms.half,
              form_key: "half",
              name: translate(language, "PAGES.USER.TUTOR.FORMS.HALF_NAME"),
            },
            {
              form: forms.full,
              form_key: "full",
              name: translate(language, "PAGES.USER.TUTOR.FORMS.FULL_NAME"),
            },
          ].map(({ form, form_key, name }) => ({
            can_complete: form.missing_graduations.some(({ id }) => id === currentUserId),
            can_generate_document: form.document_url === null && !form.missing_graduations.length,
            date: formatDateHumanly(language, form.due_date),
            document_url: form.document_url,
            form_key,
            name,
            status: form.status,
          }))
        : [],
    [currentUserId, forms, language]
  );

  const _renderFormStatus = (form: TutorredCohortFormListItem) => {
    const badgeClass = {
      completed: "tutoring-form-status-completed",
      in_progress: "tutoring-form-status-in-progress",
      pending: "tutoring-form-status-pending",
    }[form.status];
    const badge = (
      <span className={`badge tutoring-form-status xs-regular lh-1 ${badgeClass}`}>
        {translate(language, `PAGES.USER.TUTOR.FORMS.STATUS.${form.status.toUpperCase()}`)}
      </span>
    );

    const missingGraduations = forms?.[form.form_key].missing_graduations || [];
    if (form.status !== "in_progress" || !missingGraduations.length) return badge;

    return (
      <OverlayTrigger
        trigger="click"
        placement="top"
        rootClose
        overlay={
          <Popover>
            <Popover.Body className="p-2">
              <div className="xs-regular smoke-gray-font px-1 mb-2">
                {translate(language, "PAGES.USER.TUTOR.FORMS.STATUS.MISSING_GRADUATIONS")}
              </div>
              <div className="d-flex flex-column gap-1">
                {missingGraduations.map(({ id, full_name }, index) => (
                  <a
                    className="d-flex align-items-center justify-content-between gap-3 text-decoration-none"
                    href={`/user/${id}`}
                    key={`${id}-${index}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="d-flex align-items-center gap-2">
                      <span className="dst-icon-user-01" />
                      <span className="s-regular">{full_name}</span>
                    </span>
                    <span className="dst-icon-link-external-01" />
                  </a>
                ))}
              </div>
            </Popover.Body>
          </Popover>
        }
      >
        <span className="clickable">{badge}</span>
      </OverlayTrigger>
    );
  };

  const _generateFormDocument = (formKey: "half" | "full") => {
    if (!chosenCohortId) return;

    setGeneratingFormKey(formKey);
    generateUserTutorredCohortFormDocument(
      userId,
      chosenCohortId,
      formKey,
      language,
      () => fetchUserTutorredCohortForms(userId, chosenCohortId, setForms, setFormsLoading),
      () => setGeneratingFormKey(null)
    );
  };

  const _renderFormAction = (form: TutorredCohortFormListItem) => {
    const documentUrl = form.document_url;
    const isCurrentFormGenerating = generatingFormKey === form.form_key;
    const action = {
      completed: documentUrl
        ? {
            icon: "dst-icon-file-06",
            title: "PAGES.USER.TUTOR.FORMS.ACTIONS.OPEN_DOCUMENT",
            onClick: () => window.open(documentUrl, "_blank"),
          }
        : null,
      in_progress: form.can_generate_document
        ? {
            icon: "dst-icon-file-plus-02",
            title: "PAGES.USER.TUTOR.FORMS.ACTIONS.GENERATE_DOCUMENT",
            onClick: isCurrentFormGenerating ? undefined : () => _generateFormDocument(form.form_key),
          }
        : {
            icon: "dst-icon-edit-05",
            title: "PAGES.USER.TUTOR.FORMS.ACTIONS.COMPLETE",
            onClick: () => setFormToComplete(form),
          },
      pending: {
        icon: "dst-icon-hourglass-01",
        title: "PAGES.USER.TUTOR.FORMS.STATUS.PENDING",
        onClick: undefined,
      },
    }[form.status];

    if (form.status === "in_progress" && !form.can_complete && !form.can_generate_document) {
      return (
        <OverlayTrigger
          overlay={<Tooltip>{translate(language, "PAGES.USER.TUTOR.FORMS.ACTIONS.COMPLETE_DISABLED")}</Tooltip>}
        >
          <span className={`${action?.icon} smoke-gray-font`} />
        </OverlayTrigger>
      );
    }

    return action ? (
      <span
        className={`${isCurrentFormGenerating ? "dst-icon-hourglass-01 smoke-gray-font" : action.icon}${
          action.onClick ? " clickable" : ""
        }`}
        title={translate(language, action.title)}
        onClick={action.onClick}
      />
    ) : null;
  };

  return (
    <React.Fragment>
      <div className="w-100 d-flex justify-content-between mb-3 p-3">
        <div>
          {_activeTutorredCohorts.length > 1 && (
            <Form.Select
              className="w-auto s-bold my-1"
              size="sm"
              id="cohort-select"
              onChange={(event) => setSelectedCohortId(+event.target.value)}
              value={chosenCohortId}
            >
              {_activeTutorredCohorts.map((cohort) => (
                <option value={cohort.id} key={`cohort-${cohort.id}`}>
                  {cohort.name}
                </option>
              ))}
            </Form.Select>
          )}
        </div>
        <div>
          {!!chosenCohortId && (
            <React.Fragment>
              <DstButton
                btnClass="ms-1"
                value={translate(language, "PAGES.USER.TUTOR.CONTACT_COHORT_PROGRAM_MANAGER")}
                variant="primary"
                btnImageBefore={<span className="me-2 dst-icon-user-01" />}
                clickFunction={() => goToPMLink()}
                loading={loading.cohorts}
              />
              <DstButton
                btnClass="ms-1"
                value={translate(language, "PAGES.USER.TUTOR.CONTACT_COHORT_LEADER")}
                variant="secondary"
                btnImageBefore={<span className="me-2 dst-icon-mail-01" />}
                clickFunction={() =>
                  openMailClientForCohortLeader(
                    userApprenticeshipCohorts.find((cohort) => cohort.id === chosenCohortId).cohort_leader.email
                  )
                }
                loading={loading.cohorts}
              />
            </React.Fragment>
          )}
          <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/users/*/tutors" }]}>
            <DstButton
              btnClass="ms-1"
              value={translate(language, "PAGES.USER.TUTOR.MANAGE_BUTTON")}
              variant="secondary"
              btnImageBefore={<span className="me-2 dst-icon-user-01" />}
              clickFunction={() => setShowModal((prev) => ({ ...prev, history: true }))}
              loading={loading.tutors}
            />
          </PermissionComponent>
        </div>
      </div>
      <div>
        <h2>{translate(language, "PAGES.USER.TUTOR.DOCUMENT_TITLE")}</h2>

        <Lister
          items={[
            ..._defaultTutorDocuments,
            ...(chosenCohortId && tutorredCohorts[chosenCohortId]
              ? renderDocumentFilenames(
                  tutorredCohorts[chosenCohortId],
                  userApprenticeshipCohorts.find((cohort) => cohort.id === chosenCohortId)?.name,
                  language
                )
              : []),
          ]}
          columns={[{ header: translate(language, "PAGES.USER.TUTOR.DOCUMENT_LIST.NAME"), key: "name" }]}
          additionalButton={(item: any) => (
            <span className="dst-icon-search-sm clickable" onClick={() => window.open(item.link, "_blank")} />
          )}
        />
      </div>

      <div className="mt-5">
        <h2>{translate(language, "PAGES.USER.TUTOR.FORMS.TITLE")}</h2>
        <Lister
          items={_formItems}
          mock={formsLoading}
          columns={[
            { header: translate(language, "PAGES.USER.TUTOR.FORMS.COLUMNS.NAME"), key: "name" },
            { header: translate(language, "PAGES.USER.TUTOR.FORMS.COLUMNS.DATE"), key: "date" },
            {
              header: translate(language, "PAGES.USER.TUTOR.FORMS.COLUMNS.STATUS"),
              key: "status",
              render: _renderFormStatus,
            },
            {
              header: translate(language, "PAGES.USER.TUTOR.FORMS.COLUMNS.ACTIONS"),
              key: "actions",
              render: _renderFormAction,
            },
          ]}
        />
      </div>

      <TutorFormModal
        cohortId={chosenCohortId}
        form={formToComplete}
        language={language}
        onClose={() => setFormToComplete(null)}
        onSubmitted={() => {
          setFormToComplete(null);
          if (chosenCohortId) fetchUserTutorredCohortForms(userId, chosenCohortId, setForms, setFormsLoading);
        }}
        userId={userId}
      />

      <Popup
        show={showModal.history}
        onClose={() => setShowModal((prev) => ({ ...prev, history: false }))}
        modalTitle={translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.TITLE")}
      >
        <div className="d-flex flex-column">
          {tutors.length ? (
            <div className="mt-4">
              <Lister
                columns={[
                  {
                    key: "tutor_full_name",
                    header: translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.COLUMNS.FULL_NAME"),
                    render: (item: any) => item.tutor.full_name,
                  },
                  {
                    key: "tutor_email",
                    header: translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.COLUMNS.EMAIL"),
                    render: (item: any) => item.tutor.email,
                  },
                  {
                    key: "cohort_name",
                    header: translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.COLUMNS.COHORT"),
                    render: (item: any) => item.cohort.name,
                  },
                  {
                    key: "tutor_dt_added",
                    header: translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.COLUMNS.ADDED_DATE"),
                    render: (item: any) => (
                      <div>
                        <OverlayTrigger overlay={<Tooltip>{formatDateHumanly(language, item.dt_added, true)}</Tooltip>}>
                          <span>{formatDateHumanly(language, item.dt_added)}</span>
                        </OverlayTrigger>
                      </div>
                    ),
                  },
                  {
                    key: "tutor_dt_replaced",
                    header: translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.COLUMNS.REPLACED_DATE"),
                    render: (item: any) =>
                      item.dt_replaced ? (
                        <div>
                          <OverlayTrigger
                            overlay={<Tooltip>{formatDateHumanly(language, item.dt_replaced, true)}</Tooltip>}
                          >
                            <span>{formatDateHumanly(language, item.dt_replaced)}</span>
                          </OverlayTrigger>
                        </div>
                      ) : (
                        "-"
                      ),
                  },
                  {
                    key: "tutor_active",
                    header: translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.COLUMNS.ACTIVE"),
                    render: (item: any) =>
                      item.dt_replaced ? (
                        <span className="badge bg-danger">
                          {translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.INACTIVE")}
                        </span>
                      ) : (
                        <span className="badge bg-success">
                          {translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.ACTIVE")}
                        </span>
                      ),
                  },
                ]}
                items={tutors}
              />
            </div>
          ) : (
            <p className="m-regular smoke-gray-font me-1">
              {translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.NO_TUTOR")}
            </p>
          )}

          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/users/*/tutors" }]}>
            <div className="d-flex align-items-center justify-content-end mt-4">
              {!userApprenticeshipCohorts.length && (
                <span className="s-regular dark-gray-font me-2">
                  {translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.ADD_BUTTON_DISABLED_TEXT")}
                </span>
              )}
              <DstButton
                btnWidth="100px"
                variant="primary"
                value={translate(language, "PAGES.USER.TUTOR.MANAGE_MODAL.ADD_BUTTON")}
                clickFunction={() => setShowModal({ creation: true, history: false })}
                btnClass="me-2"
                btnImageBefore={<span className="me-2 dst-icon-refresh-cw-02" />}
                disabled={!userApprenticeshipCohorts.length}
              />
            </div>
          </PermissionComponent>
        </div>
      </Popup>

      <Popup
        show={showModal.creation}
        onClose={() => setShowModal({ creation: false, history: true })}
        modalTitle={translate(language, "PAGES.USER.TUTOR.CREATE_MODAL.TITLE")}
      >
        <div className="d-flex flex-column">
          <MultiSelectLiveDropdown
            category={["users"]}
            label={
              form.tutorId
                ? `${translate(language, "PAGES.USER.TUTOR.CREATE_MODAL.TUTOR_ITEM")} ${form.tutorId}`
                : translate(language, "PAGES.USER.TUTOR.CREATE_MODAL.TUTOR_PLACEHOLDER")
            }
            language={language}
            mockClass="rounded-0 rounded-end"
            toggleClass="min-w-100px m-regular"
            onConfirm={(selectedUsers: any[]) => setForm((form) => ({ ...form, tutorId: selectedUsers[0]?.value.id }))}
            isSingleSelect
          />
          <hr />
          <Dropdown>
            <Dropdown.Toggle
              className="w-100 d-flex justify-content-between align-items-center border-secondary"
              variant=""
            >
              {form.cohortId
                ? `${translate(language, "PAGES.USER.TUTOR.CREATE_MODAL.COHORT_ITEM")} ${form.cohortId}`
                : translate(language, "PAGES.USER.TUTOR.CREATE_MODAL.COHORT_PLACEHOLDER")}
            </Dropdown.Toggle>
            <Dropdown.Menu className="w-100">
              {userApprenticeshipCohorts.map((cohort) => (
                <Dropdown.Item key={cohort.id} onClick={() => setForm((form) => ({ ...form, cohortId: cohort.id }))}>
                  {cohort.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <div className="d-flex justify-content-end mt-4">
            <DstButton
              btnWidth="100px"
              variant="primary"
              disabled={!(form.tutorId && form.cohortId)}
              value={translate(language, "PAGES.USER.TUTOR.CREATE_MODAL.SUBMIT_BUTTON")}
              clickFunction={() => {
                setIsCreating(true);
                createUserTutor(
                  userId,
                  form.cohortId!,
                  form.tutorId!,
                  language,
                  () => {
                    setShowModal({ creation: false, history: true });
                    fetchUserTutors(userId, setTutors, setLoading);
                    fetchUserTutorredCohorts(userId, setTutorredCohorts, setLoading);
                    setForm({ cohortId: null, tutorId: null });
                  },
                  () => setIsCreating(false)
                );
              }}
              btnClass="me-2"
              loading={isCreating}
            />
          </div>
        </div>
      </Popup>
    </React.Fragment>
  );
};

export default UserTutors;
